import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { Button, EmptyIndicator, FileLink, ModalButton } from 'components/common';
import { Box } from 'components/common/box';

import type { Attachments } from 'shared-types';

type ModalButtonProps = Partial<React.ComponentProps<typeof Button>>;

type Props = {
  attachments: Attachments;
  isConfidential?: boolean;
} & (
  | {
      hideTitle?: boolean;
      modalButtonProps?: ModalButtonProps;
      renderInPortal?: true;
      useModalButtonPropsForTable?: boolean;
    }
  | {
      hideTitle?: never;
      modalButtonProps?: never;
      renderInPortal: false;
      useModalButtonPropsForTable?: never;
    }
);

const DisplayAttachments = ({
  attachments,
  isConfidential = false,
  hideTitle = false,
  modalButtonProps,
  renderInPortal = true,
  useModalButtonPropsForTable = false
}: Props) => {
  const { messages, theme } = useReduxState();

  const baseMessage =
    attachments.length < 2
      ? messages.commonAttachment
      : messages.commonAttachments;

  const buttonText =
    attachments.length < 2
      ? baseMessage
      : getMessageWithPlaceholders({
          message: messages.commonNumberOfAttachments,
          placeholders: { numberOfAttachments: attachments.length }
        });

  const modalButtonPropsForTable = useModalButtonPropsForTable
    ? {
        category: 'transparent' as const,
        iconStyle: {
          fontSize: '1.4rem',
          position: 'relative' as const,
          top: 1
        },
        size: 'small' as const,
        style: {
          fontSize: '1.4rem',
          padding: '4px 2px 5px 0',
          height: 28,
          width: 40
        }
      }
    : {};

  return !isConfidential && !attachments?.length ? null : (
    <div>
      {renderInPortal ? (
        <ModalButton
          icon="paperclip"
          modalContentStyle={{ backgroundColor: theme.lightGreyLighter }}
          modalProps={{
            closeButton: true,
            title: baseMessage
          }}
          {...(hideTitle
            ? { text: `${attachments.length}`, tooltip: buttonText }
            : { text: buttonText })}
          {...modalButtonPropsForTable}
          {...modalButtonProps}
        >
          <div className="attachments">
            {isConfidential ? (
              <div>
                <Box padding={0}>
                  <EmptyIndicator
                    description={messages.commonConfidentialAttachments}
                    icon="lock"
                    iconStyle={{
                      backgroundColor: theme.dangerLightColor,
                      color: theme.dangerColor
                    }}
                    size="small"
                    title={messages.commonConfidential}
                  />
                </Box>
              </div>
            ) : (
              (attachments || []).map(({ filename, fileUrl }) => (
                <div key={fileUrl} className="attachment-in-modal">
                  <Box padding="10px 15px">
                    <FileLink key={fileUrl} url={fileUrl} name={filename} />
                  </Box>
                </div>
              ))
            )}
          </div>
        </ModalButton>
      ) : (
        <div className="attachments">
          {isConfidential ? (
            <span className="attachments-confidential">
              {messages.commonConfidentialAttachments}
            </span>
          ) : (
            attachments.map(({ filename, fileUrl }) => (
              <FileLink key={fileUrl} url={fileUrl} name={filename} />
            ))
          )}
        </div>
      )}

      <style jsx>{`
        .attachments {
          color: ${theme.textColorLight};
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .attachments-confidential {
          font-weight: 400;
        }
      `}</style>
    </div>
  );
};

export default DisplayAttachments;
